import type { AudioAnalysisConfig } from "../types/core";
export type { AudioAnalysisConfig };

import { DEFAULT_AUDIO_ANALYSIS_CONFIG } from "../types/core";
export { DEFAULT_AUDIO_ANALYSIS_CONFIG };

interface AudioAnalysisData {
	volume: number; // 0-100 scale
	isSpeaking: boolean;
	silenceDuration: number; // milliseconds of current silence
}

/**
 * Real-time audio analysis for volume detection and speech rate monitoring
 * Implements Requirements 4.1, 4.2, 4.3
 */
export class AudioAnalyzer {
	private audioContext: AudioContext | null = null;
	private analyser: AnalyserNode | null = null;
	private microphone: MediaStreamAudioSourceNode | null = null;
	private dataArray: Uint8Array | null = null;
	private config: AudioAnalysisConfig;

	// Analysis state
	private currentVolume = 0;
	private silenceStartTime = 0;
	private isSpeaking = false;

	// Callbacks
	private onAnalysisUpdate: ((data: AudioAnalysisData) => void) | null = null;
	private onVolumeChange: ((volume: number) => void) | null = null;
	private onSpeechStateChange:
		| ((isSpeaking: boolean, silenceDuration: number) => void)
		| null = null;
	private onSpeechSegmentEnd: (() => void) | null = null;

	// Analysis loop
	private analysisInterval: number | null = null;
	private isAnalyzing = false;
	private hasTriggeredSegmentEnd = false;

	constructor(config: AudioAnalysisConfig = DEFAULT_AUDIO_ANALYSIS_CONFIG) {
		this.config = config;
	}

	/**
	 * Initialize audio analysis with microphone stream
	 */
	async initialize(stream: MediaStream): Promise<void> {
		try {
			// Create audio context
			const AudioContextClass =
				window.AudioContext ||
				(window as Window & { webkitAudioContext?: typeof AudioContext })
					.webkitAudioContext;
			if (!AudioContextClass) {
				throw new Error("AudioContext is not supported in this browser");
			}
			this.audioContext = new AudioContextClass();

			// Create analyser node
			this.analyser = this.audioContext.createAnalyser();
			this.analyser.fftSize = 256;
			this.analyser.smoothingTimeConstant = this.config.smoothingFactor;

			// Connect microphone to analyser
			this.microphone = this.audioContext.createMediaStreamSource(stream);
			this.microphone.connect(this.analyser);

			// Initialize data array for frequency analysis
			const bufferLength = this.analyser.frequencyBinCount;
			this.dataArray = new Uint8Array(bufferLength);

			console.info("Audio analyzer initialized successfully");
		} catch (error) {
			throw new Error(
				`Failed to initialize audio analyzer: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		}
	}

	/**
	 * Start real-time audio analysis
	 */
	startAnalysis(): void {
		if (!this.analyser || !this.dataArray) {
			throw new Error("Audio analyzer not initialized");
		}

		if (this.isAnalyzing) {
			return;
		}

		this.isAnalyzing = true;
		this.silenceStartTime = Date.now();

		// Start analysis loop
		this.analysisInterval = window.setInterval(() => {
			this.performAnalysis();
		}, this.config.analysisInterval);

		console.info("Audio analysis started");
	}

	/**
	 * Stop audio analysis
	 */
	stopAnalysis(): void {
		if (this.analysisInterval) {
			clearInterval(this.analysisInterval);
			this.analysisInterval = null;
		}

		this.isAnalyzing = false;
		console.info("Audio analysis stopped");
	}

	/**
	 * Perform real-time audio analysis
	 */
	private performAnalysis(): void {
		if (!this.analyser) {
			return;
		}

		// Performance optimization: Reuse the existing buffer instead of allocating a new one
		// on every analysis cycle (every 100ms by default). This reduces GC pressure.
		// We ensure the buffer exists and matches the current frequency bin count.
		if (
			!this.dataArray ||
			this.dataArray.length !== this.analyser.frequencyBinCount
		) {
			this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
		}

		// Get frequency data directly into the pre-allocated buffer
		this.analyser.getByteFrequencyData(
			this.dataArray as Uint8Array<ArrayBuffer>,
		);

		// Calculate current volume (RMS of frequency data)
		let sum = 0;
		for (let i = 0; i < this.dataArray.length; i++) {
			sum += this.dataArray[i] * this.dataArray[i];
		}
		const rms = Math.sqrt(sum / this.dataArray.length);
		this.currentVolume = Math.min(100, (rms / 255) * 100);

		// Detect speech state
		const now = Date.now();
		const wasSpeaking = this.isSpeaking;
		this.isSpeaking = this.currentVolume > this.config.volumeThreshold;

		// Update speech timing
		if (this.isSpeaking) {
			if (!wasSpeaking) {
				// Speech started
				this.silenceStartTime = 0;
				this.hasTriggeredSegmentEnd = false;
			}
		} else if (wasSpeaking) {
			// Speech just stopped
			this.silenceStartTime = now;
		}

		// Calculate silence duration
		const silenceDuration =
			this.silenceStartTime > 0 ? now - this.silenceStartTime : 0;

		// Trigger segment end if silence exceeds threshold
		if (
			!this.isSpeaking &&
			silenceDuration >= this.config.silenceThreshold &&
			!this.hasTriggeredSegmentEnd
		) {
			this.hasTriggeredSegmentEnd = true;
			if (this.onSpeechSegmentEnd) {
				this.onSpeechSegmentEnd();
			}
		}

		// Create analysis data
		const analysisData: AudioAnalysisData = {
			volume: this.currentVolume,
			isSpeaking: this.isSpeaking,
			silenceDuration,
		};

		// Trigger callbacks
		if (this.onAnalysisUpdate) {
			this.onAnalysisUpdate(analysisData);
		}

		if (this.onVolumeChange) {
			this.onVolumeChange(this.currentVolume);
		}

		if (this.onSpeechStateChange && wasSpeaking !== this.isSpeaking) {
			this.onSpeechStateChange(this.isSpeaking, silenceDuration);
		}
	}

	/**
	 * Set callback for analysis updates
	 */
	onAnalysis(callback: (data: AudioAnalysisData) => void): void {
		this.onAnalysisUpdate = callback;
	}

	/**
	 * Set callback for volume changes
	 */
	onVolume(callback: (volume: number) => void): void {
		this.onVolumeChange = callback;
	}

	/**
	 * Set callback for speech state changes
	 */
	onSpeechState(
		callback: (isSpeaking: boolean, silenceDuration: number) => void,
	): void {
		this.onSpeechStateChange = callback;
	}

	/**
	 * Set callback for speech segment completion (when silence exceeds threshold)
	 */
	onSpeechSegment(callback: () => void): void {
		this.onSpeechSegmentEnd = callback;
	}

	/**
	 * Get current analysis data
	 */
	getCurrentAnalysis(): AudioAnalysisData {
		const now = Date.now();
		const silenceDuration =
			this.silenceStartTime > 0 ? now - this.silenceStartTime : 0;

		return {
			volume: this.currentVolume,
			isSpeaking: this.isSpeaking,
			silenceDuration,
		};
	}

	/**
	 * Update configuration
	 */
	updateConfig(newConfig: Partial<AudioAnalysisConfig>): void {
		this.config = { ...this.config, ...newConfig };

		// Update analyser smoothing if available
		if (this.analyser) {
			this.analyser.smoothingTimeConstant = this.config.smoothingFactor;
		}

		// Restart analysis with new interval if needed
		if (newConfig.analysisInterval && this.isAnalyzing) {
			this.stopAnalysis();
			this.startAnalysis();
		}
	}

	/**
	 * Get current configuration
	 */
	getConfig(): AudioAnalysisConfig {
		return { ...this.config };
	}

	/**
	 * Get analysis statistics
	 */
	getStats(): {
		isAnalyzing: boolean;
		currentVolume: number;
	} {
		return {
			isAnalyzing: this.isAnalyzing,
			currentVolume: this.currentVolume,
		};
	}

	/**
	 * Reset analysis state
	 */
	reset(): void {
		this.currentVolume = 0;
		this.silenceStartTime = Date.now();
		this.isSpeaking = false;
	}

	/**
	 * Check if audio analysis is supported
	 */
	static isSupported(): boolean {
		return !!(
			window.AudioContext ||
			(window as Window & { webkitAudioContext?: typeof AudioContext })
				.webkitAudioContext
		);
	}

	/**
	 * Cleanup resources
	 */
	destroy(): void {
		this.stopAnalysis();

		if (this.microphone) {
			this.microphone.disconnect();
			this.microphone = null;
		}

		if (this.analyser) {
			this.analyser.disconnect();
			this.analyser = null;
		}

		if (this.audioContext) {
			this.audioContext.close();
			this.audioContext = null;
		}

		this.dataArray = null;
		this.onAnalysisUpdate = null;
		this.onVolumeChange = null;
		this.onSpeechStateChange = null;
	}
}
